// Sentence-builder puzzles for the Grammar Playground.
//
// `tokens`     — the words/chunks that compose the target sentence, in target order.
// `verbIndex`  — position of the finite verb in the target. The V2 rule says it
//                should always be in slot 1 (i.e. index 1, the second element)
//                of a Norwegian main clause.
// The playground shuffles tokens for the player and rewards landing the verb
// in `verbIndex`.

export const sentencePuzzles = [
  {
    id: 'jeg-snakker-litt-norsk',
    target: 'Jeg snakker litt norsk',
    english: 'I speak a little Norwegian',
    literal: 'I speak little Norwegian',
    tokens: ['Jeg', 'snakker', 'litt', 'norsk'],
    verbIndex: 1,
    rule: 'V2 rule: the finite verb is always the second element in a main clause. Here the verb "snakker" sits right after the subject.',
  },
  {
    id: 'jeg-heter-anna',
    target: 'Jeg heter Anna',
    english: 'My name is Anna',
    literal: 'I am-called Anna',
    tokens: ['Jeg', 'heter', 'Anna'],
    verbIndex: 1,
    rule: 'Norwegian uses a verb ("heter" — am called) where English uses a possessive ("My name is"). The verb still sits in slot 2.',
  },
  {
    id: 'i-dag-er-jeg-trott',
    target: 'I dag er jeg trøtt',
    english: 'Today I am tired',
    literal: 'Today am I tired',
    tokens: ['I dag', 'er', 'jeg', 'trøtt'],
    verbIndex: 1,
    rule: 'When a sentence opens with a time word like "I dag", the verb STILL takes slot 2 — so the subject and verb swap places. This is called inversion.',
  },
  {
    id: 'hun-har-en-katt',
    target: 'Hun har en katt',
    english: 'She has a cat',
    literal: 'She has a cat',
    tokens: ['Hun', 'har', 'en', 'katt'],
    verbIndex: 1,
    rule: 'Classic V2 ordering: Subject → Verb → Object.',
  },
  {
    id: 'vi-skal-til-oslo',
    target: 'Vi skal til Oslo',
    english: 'We are going to Oslo',
    literal: 'We shall to Oslo',
    tokens: ['Vi', 'skal', 'til', 'Oslo'],
    verbIndex: 1,
    rule: 'Norwegian uses "skal" (shall) where English says "going to". Note how "til" works like English "to" for destinations.',
  },
  {
    id: 'jeg-forstaar-ikke',
    target: 'Jeg forstår ikke',
    english: "I don't understand",
    literal: 'I understand not',
    tokens: ['Jeg', 'forstår', 'ikke'],
    verbIndex: 1,
    rule: '"Ikke" (not) always comes AFTER the verb in a main clause — never before it. English speakers tend to put "not" earlier; resist the urge.',
  },
  {
    id: 'jeg-kan-snakke-norsk',
    target: 'Jeg kan snakke norsk',
    english: 'I can speak Norwegian',
    literal: 'I can speak Norwegian',
    tokens: ['Jeg', 'kan', 'snakke', 'norsk'],
    verbIndex: 1,
    rule: 'With a modal verb ("kan", "vil", "skal", "må"), only the MODAL counts as the V2 verb. The second verb stays in its bare infinitive form — no "å".',
  },
  {
    id: 'her-bor-jeg',
    target: 'Her bor jeg',
    english: 'I live here',
    literal: 'Here live I',
    tokens: ['Her', 'bor', 'jeg'],
    verbIndex: 1,
    rule: 'Fronting a place word ("her") still keeps the verb in slot 2 — so the subject "jeg" is pushed back to slot 3. Inversion again.',
  },
  {
    id: 'har-du-tid',
    target: 'Har du tid?',
    english: 'Do you have time?',
    literal: 'Have you time?',
    tokens: ['Har', 'du', 'tid'],
    verbIndex: 0,
    rule: 'In yes/no questions the verb leaps to position 1 — there is no "do" helper in Norwegian. The verb itself is enough.',
  },
  {
    id: 'hva-heter-du',
    target: 'Hva heter du?',
    english: "What's your name?",
    literal: 'What are-called you?',
    tokens: ['Hva', 'heter', 'du'],
    verbIndex: 1,
    rule: 'In wh-questions the question word fills slot 1 and the verb still takes slot 2. Subject follows.',
  },
  {
    id: 'jeg-liker-kaffe',
    target: 'Jeg liker kaffe',
    english: 'I like coffee',
    literal: 'I like coffee',
    tokens: ['Jeg', 'liker', 'kaffe'],
    verbIndex: 1,
    rule: 'Plain S-V-O. "Liker" is a regular -er verb in the present tense.',
  },
  {
    id: 'i-gaar-spiste-vi-pizza',
    target: 'I går spiste vi pizza',
    english: 'Yesterday we ate pizza',
    literal: 'Yesterday ate we pizza',
    tokens: ['I går', 'spiste', 'vi', 'pizza'],
    verbIndex: 1,
    rule: 'Past-tense inversion: fronted time phrase + past-tense verb in slot 2 + subject in slot 3.',
  },
  {
    id: 'jeg-har-bodd-i-oslo',
    target: 'Jeg har bodd i Oslo',
    english: 'I have lived in Oslo',
    literal: 'I have lived in Oslo',
    tokens: ['Jeg', 'har', 'bodd', 'i', 'Oslo'],
    verbIndex: 1,
    rule: 'In the perfect tense "har" is the V2 verb. The participle ("bodd") follows but does not count for V2.',
  },
  {
    id: 'hun-snakker-veldig-bra',
    target: 'Hun snakker veldig bra',
    english: 'She speaks very well',
    literal: 'She speaks very well',
    tokens: ['Hun', 'snakker', 'veldig', 'bra'],
    verbIndex: 1,
    rule: 'Manner adverbs ("bra", "fort", "stille") sit AFTER the verb and any object. Intensifiers like "veldig" cling to the adverb they modify.',
  },
  {
    id: 'jeg-spiser-og-hun-drikker',
    target: 'Jeg spiser og hun drikker',
    english: 'I eat and she drinks',
    literal: 'I eat and she drinks',
    tokens: ['Jeg', 'spiser', 'og', 'hun', 'drikker'],
    verbIndex: 1,
    rule: 'Each main clause obeys V2 independently. The connector "og" (and) does NOT count as a slot for V2 in the second clause.',
  },
  {
    id: 'det-er-kaldt-i-dag',
    target: 'Det er kaldt i dag',
    english: "It's cold today",
    literal: 'It is cold in day',
    tokens: ['Det', 'er', 'kaldt', 'i', 'dag'],
    verbIndex: 1,
    rule: 'Norwegian uses a dummy "det" for weather and impersonal statements — exactly like English "it". Verb still in slot 2.',
  },
];

export function puzzleById(id) {
  return sentencePuzzles.find(p => p.id === id);
}
